import type {
  ParticipantBehaviour,
  ParticipantBehaviourRecording,
  UpdateParticipantBehaviourRecordingRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover, ScrollArea } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaEdit } from 'react-icons/fa';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { useUpdateParticipantBehaviourRecordingMutation } from '../../../graphql/hooks/useUpdateParticipantBehaviourRecordingMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import type { MethodType } from './MethodTag';
import { participantBehaviourRecordingCreateSchema } from './ParticipantBehaviourRecordingCreateButton';
import { ParticipantBehaviourRecordingForm } from './ParticipantBehaviourRecordingForm';

type FormValues = UpdateParticipantBehaviourRecordingRequest & {
  participantBehaviourId: string;
  method: MethodType;
};

interface ParticipantBehaviourRecordingEditContentProps {
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
  recording: ParticipantBehaviourRecording;
  onClose: () => void;
}

const ParticipantBehaviourRecordingEditContent = (
  props: ParticipantBehaviourRecordingEditContentProps,
) => {
  const { participantId, participantBehaviours, recording, onClose } = props;

  const { organization, successToast, errorToast } = useAppContext();

  const mutation = useUpdateParticipantBehaviourRecordingMutation();

  const initialValues: Partial<FormValues> = {
    participantBehaviourId: recording.participantBehaviourId,
    comment: recording.comment,
    timestamp: recording.timestamp,
    method: recording.method.__typename,
    payload: P.run(() => {
      switch (recording.__typename) {
        case 'ParticipantBehaviourRecordingAbc':
          return {
            abc: {
              antecedent: recording.antecedent,
              behaviour: recording.behaviour,
              consequence: recording.consequence,
            },
          };
        case 'ParticipantBehaviourRecordingFrequency':
          return { frequency: { value: recording.value } };
        case 'ParticipantBehaviourRecordingDuration':
          return { duration: { value: recording.value } };
        case 'ParticipantBehaviourRecordingEpisodicSeverity':
          return { episodicSeverity: { level: recording.value } };
        default:
          return {};
      }
    }),
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(participantBehaviourRecordingCreateSchema),
  });

  const { reset } = methods;

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const payload = P.run(() => {
        switch (values.method) {
          case 'AbcMethod':
            return { abc: values.payload?.abc };
          case 'FrequencyMethod':
            return { frequency: values.payload?.frequency };
          case 'DurationMethod':
            return { duration: values.payload?.duration };
          case 'EpisodicSeverityMethod':
            return { episodicSeverity: values.payload?.episodicSeverity };
          default:
            return {};
        }
      });

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId: participantId,
          participantBehaviourId: values.participantBehaviourId,
          recordingId: recording.id,
          request: {
            payload,
            timestamp: values.timestamp,
          },
        })
        .then(async () => {
          successToast('Participant behaviour recording updated successfully');
          onClose();
          reset();
        })
        .catch((err) => {
          errorToast(`Error updating participant behaviour recording: ${err.message}`);
        });
    },
    [
      mutation,
      organization.id,
      participantId,
      recording.id,
      successToast,
      onClose,
      reset,
      errorToast,
    ],
  );

  return (
    <ChakraV3.Stack minH='xs' w='xl' bg='white' gap={0} shadow='2xl' rounded='xl'>
      <ChakraV3.HStack px={3} py={3} borderBottomWidth='1px'>
        <ChakraV3.Text fontSize='md' fontWeight='semibold'>
          Edit recording
        </ChakraV3.Text>
      </ChakraV3.HStack>

      <FormProvider {...methods}>
        <ScrollArea maxH='3xl' h='full'>
          <ChakraV3.Stack as='form' px={4} py={4} gap={8} onSubmit={methods.handleSubmit(onSubmit)}>
            <ParticipantBehaviourRecordingForm
              participantBehaviours={participantBehaviours}
              isChangeBehaviourDisabled
              isChangeMethodDisabled
            />
            <HookedSubmitButton disabled={false}>
              <FaEdit />
              Edit data
            </HookedSubmitButton>
          </ChakraV3.Stack>
        </ScrollArea>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
  recording: ParticipantBehaviourRecording;
}

export const ParticipantBehaviourRecordingEditButton = (props: Props) => {
  const { participantId, participantBehaviours, recording } = props;

  const disclosure = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={disclosure.open}
      setOpen={(isOpen) => (isOpen ? disclosure.onOpen() : disclosure.onClose())}
      render={() => (
        <ParticipantBehaviourRecordingEditContent
          participantId={participantId}
          participantBehaviours={participantBehaviours}
          recording={recording}
          onClose={disclosure.onClose}
        />
      )}
    >
      <ChakraV3.Button size='xs' variant='plain'>
        Edit
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
